import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { arrayOf, number, shape } from 'prop-types';

import * as R from 'ramda';
import Box from '@mui/material/Box';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';

import { useGetGlobalCustomersQuery } from 'feature/subscription-plans/enrollment/enrollmentEndpoints';

import {
  defaultMapCenter,
  defaultMapTheme,
  defaultMapViewOptions,
  defaultMapZoom,
} from './mapDefaults';
import buildPin from './Pin';
import './Map.css';

const styles = {
  '& .esri-popup--shadow': {
    '&:after': {
      bottom: 0,
      boxShadow: 1,
      content: '""',
      display: 'block',
      left: 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: 0,
      top: 0,
    },
    boxShadow: 6,
  },
  '& .esri-popup__content': {
    margin: 0,
    padding: 2,
  },
  '& .esri-popup__main-container': {
    borderRadius: { lg: 0.5, xs: 0 },
    maxWidth: { lg: '320px', xs: '100%' },
  },
  '& .esri-popup__main-container, .esri-popup--is-docked .esri-popup__header': {
    padding: '0 !important',
  },
  '& .map-popup__address, & .map-popup__distance': {
    typography: 'body2',
  },
  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
  '& .map-popup__address': {
    color: 'text.secondary',
  },
  '& .map-popup__terminal-id-name': {
    ':hover': {
      backgroundColor: 'secondary.light',
    },
    alignSelf: 'flex-start',
    typography: 'label',
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    color: 'text.primary',
    justifyContent: 'flex-start',
    mb: 0.75,
    ml: -1,
    mr: 0,
    mt: 0,
    pb: 0.25,
    pt: 0.75,
    px: 1,
  },
  '& .map-popup__time-banner': {
    '& .time-banner--clock': {
      backgroundColor: 'surface.light',
    },
    '& .time-banner--time-high': {
      backgroundColor: 'error.light',
    },
    '& .time-banner--time-low': {
      backgroundColor: 'success.light',
    },
    '& .time-banner--time-med': {
      backgroundColor: 'warning.main',
    },
    '& .time-banner--time-none': {
      backgroundColor: 'graphic.light',
    },
  },
  // inset map shadow
  '&:after': {
    boxShadow: 5,
    content: '""',
    display: 'block',
    height: '8px',
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: '-8px',
  },
  height: '100%',
};

const Map = ({ location, mapCenter, selectedTerminal, terminals }) => {
  const [terminalsState, setTerminalsState] = useState([]);
  const mapContainerRef = useRef(null);
  const webMapRef = useRef(null);
  const mapViewRef = useRef(null);
  const graphicsLayerRef = useRef(null);

  const { data: globalCustomersQuery = [] } = useGetGlobalCustomersQuery();
  const hasAffiliateWithSubscription = globalCustomersQuery.some(affiliate =>
    R.equals(affiliate.hasActiveSubscription, true)
  );

  const closePopups = () => mapViewRef.current.popup.close();

  // center map on selected terminal & open popup
  const showSelectedTerminal = terminalId => {
    const terminal = graphicsLayerRef.current.graphics.find(
      t => t.attributes.terminalId === terminalId
    );

    if (terminal) {
      mapViewRef.current.goTo(terminal).then(() => {
        mapViewRef.current.popup.open({
          fetchFeatures: true, // load popup features from marker at this geometry
          location: terminal.geometry,
        });
      });
    }
  };

  const { hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (hash.length) {
      const { latitude, longitude } = { ...location };
      const terminalDetailsUrl =
        latitude && longitude ? hash.concat(`?latitude=${latitude}&longitude=${longitude}`) : hash;
      navigate(terminalDetailsUrl.replace('#', ''), { replace: true });
    }
  }, [hash, location, navigate]);

  // initialize map
  useEffect(() => {
    webMapRef.current = new WebMap({
      basemap: defaultMapTheme,
    });

    mapViewRef.current = new MapView({
      container: mapContainerRef.current,
      map: webMapRef.current,
      zoom: defaultMapZoom,
      ...defaultMapViewOptions,
    });

    graphicsLayerRef.current = new GraphicsLayer();
    webMapRef.current.add(graphicsLayerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // set map center
  useEffect(() => {
    if (mapViewRef.current) {
      closePopups();
      const { latitude, longitude } = mapCenter;
      mapViewRef.current.center = { latitude, longitude };
      mapViewRef.current.zoom = defaultMapZoom;
    }
  }, [mapCenter]);

  // build map pins
  useEffect(() => {
    if (webMapRef.current && mapViewRef.current && R.not(R.equals(terminalsState, terminals))) {
      graphicsLayerRef.current.removeAll(); // remove any existing open popups

      terminals.map(terminal => {
        const pin = buildPin(hasAffiliateWithSubscription, terminal);

        return graphicsLayerRef.current.add(pin);
      });
    }

    setTerminalsState(terminals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminals]);

  // handle terminal selection from terminal list
  useEffect(() => {
    if (selectedTerminal) {
      closePopups();
      showSelectedTerminal(selectedTerminal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerminal]);

  return <Box ref={mapContainerRef} sx={styles} />;
};

Map.propTypes = {
  location: shape({
    latitude: number,
    longitude: number,
  }),
  mapCenter: shape({
    latitude: number,
    longitude: number,
  }),
  selectedTerminal: number,
  terminals: arrayOf(shape({})),
};

Map.defaultProps = {
  location: null,
  mapCenter: defaultMapCenter,
  selectedTerminal: null,
  terminals: [],
};

export default Map;
